<!--
  STRUTTURA PRINCIPALE DEL COMPONENTE (Main Component Structure)

  Questo componente Vue gestisce un form per la creazione di incidenti.

  Il componente ha due stati principali:
  1. Stato di conferma (quando l'incidente è stato creato)
  2. Stato del form (quando l'utente sta compilando i dati)
-->
<template>
  <!--
    Componente di conferma mostrato dopo la creazione dell'incidente
  -->
  <HelpRequestConfirmed
    :confirmedRequestId="this.incidentId"
    v-if="this.incidentId"
  >
    <template #tail>
      <!--
        Pulsante per tornare alla lista degli incidenti
      -->
      <div class="contentButton">
        <div
          class="cdlButtonGreen"
          style="width: auto"
          @click="
            this.$router.push({
              name: this.backToListUrl,
            })
          "
        >
          {{ $t("colocation.backToList") }}
        </div>
      </div>
    </template>
  </HelpRequestConfirmed>

  <!--
    Form principale per la creazione dell'incidente
  -->
  <div id="incidentForm" v-else>
    <!--
      Form standard (non-HubSpot) - Questa sezione contiene il form personalizzato
    -->
    <div class="contentFormTwoCol" v-if="!isHub">
      <!--
        Componente che mostra i dettagli della soluzione/asset per cui si sta creando l'incidente
      -->
      <HelpRequestSolution
        :name="
          this.solution && this.asset_type == 'cross-connects'
            ? this.solution.tipo
            : this.solution.name
            ? this.solution.name
            : this.solution.solution_type != 'voip'
            ? this.solution.solution_type + ' - ' + this.solution.solution
            : this.solution.solution
        "
        :address="this.solution?.address"
        :dc="this.solution?.room"
      />
      <div class="contentSecondary">
        <!--
          Avviso di ticket duplicato - Mostrato se esiste già un ticket per questo asset
        -->
        <div class="mdl-grid padding0">
          <div
            class="mdl-cell mdl-cell--12-col alert-container"
            v-if="hasDuplicateTicket"
          >
            <span class="text-alert-danger">
              {{ $t("help.duplicatedTicketAlert") }}
              <span
                v-if="duplicateTicketIds.length"
                class="duplicate-ticket-links"
              >
                {{ $t("help.duplicatedTicketListLabel") }}
                <template v-for="(ticketId, index) in duplicateTicketIds" :key="ticketId">
                  <router-link :to="buildIncidentTicketLink(ticketId)">
                    {{ ticketId }}
                  </router-link>
                  <span v-if="index < duplicateTicketIds.length - 1">,&nbsp;</span>
                </template>
              </span>
            </span>
          </div>
        </div>
        <div class="mdl-grid padding0">
          <div class="mdl-cell mdl-cell--12-col">
            <h2 class="title">{{ $t("help.incident.formTitle") }}</h2>
          </div>

          <div class="mdl-cell mdl-cell--12-col">
            <span class="labelTwo">{{ $t("help.mainInfo") }}</span>
          </div>

          <!--
            Campo Titolo - Input di testo con validazione
          -->
          <div class="mdl-cell mdl-cell--6-col">
            <div class="cdl-input">
              <div class="mdl-cell mdl-cell--12-col">
                <span class="labelTwo">{{ $t("help.title") }}*</span>
              </div>
              <input
                type="text"
                maxlength="100"
                name="title"
                v-model="fields.title"
                @input="label($event)"
              />
              <!--
                Messaggio di errore per validazione
              -->
              <span
                class="error"
                v-if="
                  this.v$.fields.title.$invalid &&
                  this.v$.fields.title.$dirty &&
                  this.v$.fields.title.required.$invalid
                "
              >
                {{ $t("help.title") }}
                {{ $t("checkCoverage.isRequired") }}
              </span>
            </div>
          </div>
          <!--
            Campo Categoria - Dropdown menu mostrato solo per asset di tipo 'colocation'
          -->
          <div
            class="mdl-cell mdl-cell--6-col"
            v-show="this.assetTypeFromParams === 'colocation'"
          >
            <div class="mdl-cell mdl-cell--12-col">
              <span class="labelTwo">{{ $t("help.incident.category") }}*</span>
            </div>
            <div class="cdl-input">
              <select
                name="category"
                @input="label($event)"
                style="margin-left: 0"
                v-model="fields.category"
              >
                <!--
                  Opzioni generate dinamicamente dalla lista categories
                -->
                <option
                  :value="category.id"
                  v-for="category in this.categories"
                  :key="category.id"
                >
                  {{ category.value }}
                </option>
              </select>
              <span
                class="error"
                v-if="
                  this.v$.fields.severity_level.$invalid &&
                  this.v$.fields.severity_level.$dirty
                "
              >
                {{ $t("help.incident.category") }}
                {{ $t("checkCoverage.isRequired") }}
              </span>
            </div>
          </div>
          <div class="mdl-cell mdl-cell--6-col"></div>
          <!--
            Campo Descrizione - Area di testo con validazione
          -->
          <div class="mdl-cell mdl-cell--12-col">
            <div class="cdl-textarea">
              <div class="mdl-cell mdl-cell--12-col">
                <span class="labelTwo">{{ $t("help.description") }}*</span>
              </div>
              <textarea
                maxlength="1000"
                name="description"
                id="textareaTwo"
                v-model="fields.description"
                @input="label($event)"
              ></textarea>
              <span
                class="error"
                v-if="
                  this.v$.fields.description.$invalid &&
                  this.v$.fields.description.$dirty &&
                  this.v$.fields.description.required.$invalid
                "
              >
                {{ $t("help.description") }}
                {{ $t("checkCoverage.isRequired") }}
              </span>
            </div>
          </div>
        </div>
        <hr />

        <!--
          Sezione Livello di Severità - Dropdown per selezionare l'urgenza dell'incidente
        -->
        <div class="mdl-grid padding0">
          <div class="mdl-cell mdl-cell--12-col">
            <span class="labelTwo"
              >{{ $t("help.incident.severityLevel") }}*</span
            >
          </div>
          <div class="mdl-cell mdl-cell--6-col">
            <div class="cdl-input">
              <select
                name="severity_level"
                @input="label($event)"
                style="margin-left: 0"
                v-model="fields.severity_level"
              >
                <!--
                  Opzioni di severità generate dinamicamente
                -->
                <option
                  v-for="level in severityLevels"
                  :key="level"
                  :value="level"
                >
                  {{ $t("help.incident.severityLevel" + level) }}
                </option>
              </select>
              <span
                class="error"
                v-if="
                  this.v$.fields.severity_level.$invalid &&
                  this.v$.fields.severity_level.$dirty
                "
              >
                {{ $t("help.incident.severityLevel") }}
                {{ $t("checkCoverage.isRequired") }}
              </span>
            </div>
          </div>
        </div>

        <hr />

        <!--
          Sezione Contatto Secondario - Email opzionale per un contatto aggiuntivo
        -->
        <div class="mdl-grid">
          <div class="mdl-cell mdl-cell--12-col">
            <span class="labelTwo">{{ $t("help.secondContact") }}</span>
          </div>
          <div class="mdl-cell mdl-cell--6-col">
            <div class="cdl-input">
              <div class="mdl-cell mdl-cell--12-col">
                <span class="labelTwo">{{
                  $t("help.secondContactLabel")
                }}</span>
              </div>
              <input
                type="email"
                name="other_email"
                v-model="fields.other_email"
                @input="label($event)"
              />
              <!-- Validazione email - Mostra errore se il formato non è valido -->
              <span
                class="error"
                v-if="
                  this.v$.fields.other_email.$invalid &&
                  this.v$.fields.other_email.$dirty
                "
              >
                {{ $t("help.secondContact") }}
                {{ $t("help.invalidEmail") }}
              </span>
            </div>
          </div>
        </div>

        <hr />

        <!--
          Sezione Allegati - Permette di caricare file allegati all'incidente
        -->
        <div class="mdl-grid">
          <div class="mdl-cell mdl-cell--12-col">
            <p class="labelTwo" style="margin-bottom: 0">
              {{
                $t("shipping.uploadAttach", { format: getFormatNameAttach() })
              }}
            </p>
            <span class="subTitleAttach">{{
              $t("shipping.uploadAttachSubTitle", {
                total: this.nAttach,
                size: getLimitSizeAttach(),
              })
            }}</span>
            <br />
            <!-- Messaggi di errore per formato file non valido o dimensione eccessiva -->
            <span class="error" v-if="errorAttach">
              {{
                $t("shipping.attachFormat", { format: getFormatNameAttach() })
              }}
            </span>
            <span class="error" v-if="errorAttachSize">
              {{ $t("shipping.attachSize", { size: getLimitSizeAttach() }) }}
            </span>
            <br />
            <br />
          </div>
          <!--
            Lista di allegati - Interfaccia per caricare fino a 5 file
          -->
          <div class="mdl-cell mdl-cell--12-col listAttach">
            <!--
              Primo slot allegato - Gestisce selezione, visualizzazione e rimozione del file
            -->
            <div
              :class="{
                hover:
                  this.fields.attachments.length == 0 ||
                  this.fields.attachments[0],
              }"
              v-if="this.nAttach >= 1"
            >
              <!-- Icone diverse in base allo stato (con o senza file) -->
              <img
                src="@/assets/img/shipping/free_attach.png"
                alt="attach"
                v-if="this.fields.attachments.length == 0"
              />
              <img
                src="@/assets/img/shipping/complete_attach.png"
                alt="attach"
                v-if="this.fields.attachments[0]"
              />
              <!--
                Pulsante per rimuovere l'allegato
              -->
              <span
                class="material-icons deleteAttach"
                v-if="this.fields.attachments[0]"
                @click="removeAttach(0)"
                >cancel</span
              >
              <!-- Nome del file caricato -->
              <p v-if="this.fields.attachments[0]" class="fileName">
                {{ this.fields.attachments[0].name }}
              </p>
              <!--
                Input file nascosto - Gestisce la selezione del file
              -->
              <input
                type="file"
                @change="onFileChanged($event, 0)"
                :accept="getFormatAttach()"
                capture
                class="fileInput"
              />
            </div>
            <div
              :class="{
                hover: this.fields.attachments[0] || this.fields.attachments[1],
              }"
              v-if="this.nAttach >= 2"
            >
              <img
                src="@/assets/img/shipping/free_attach.png"
                alt="attach"
                v-if="this.fields.attachments[0] && !this.fields.attachments[1]"
              />
              <img
                src="@/assets/img/shipping/no_attach.png"
                alt="attach"
                v-if="
                  !this.fields.attachments[0] && !this.fields.attachments[1]
                "
              />
              <img
                src="@/assets/img/shipping/complete_attach.png"
                alt="attach"
                v-if="this.fields.attachments[1]"
              />
              <span
                class="material-icons deleteAttach"
                v-if="this.fields.attachments[1]"
                @click="removeAttach(1)"
                >cancel</span
              >
              <p v-if="this.fields.attachments[1]" class="fileName">
                {{ this.fields.attachments[1].name }}
              </p>
              <input
                type="file"
                @change="onFileChanged($event, 1)"
                :accept="getFormatAttach()"
                capture
                class="fileInput"
              />
            </div>
            <div
              :class="{
                hover: this.fields.attachments[1] || this.fields.attachments[2],
              }"
              v-if="this.nAttach >= 3"
            >
              <img
                src="@/assets/img/shipping/free_attach.png"
                alt="attach"
                v-if="this.fields.attachments[1] && !this.fields.attachments[2]"
              />
              <img
                src="@/assets/img/shipping/no_attach.png"
                alt="attach"
                v-if="
                  !this.fields.attachments[1] && !this.fields.attachments[2]
                "
              />
              <img
                src="@/assets/img/shipping/complete_attach.png"
                alt="attach"
                v-if="this.fields.attachments[2]"
              />
              <span
                class="material-icons deleteAttach"
                v-if="this.fields.attachments[2]"
                @click="removeAttach(2)"
                >cancel</span
              >
              <p v-if="this.fields.attachments[2]" class="fileName">
                {{ this.fields.attachments[2].name }}
              </p>
              <input
                type="file"
                @change="onFileChanged($event, 2)"
                :accept="getFormatAttach()"
                capture
                class="fileInput"
              />
            </div>
            <div
              :class="{
                hover: this.fields.attachments[2] || this.fields.attachments[3],
              }"
              v-if="this.nAttach >= 4"
            >
              <img
                src="@/assets/img/shipping/free_attach.png"
                alt="attach"
                v-if="this.fields.attachments[2] && !this.fields.attachments[3]"
              />
              <img
                src="@/assets/img/shipping/no_attach.png"
                alt="attach"
                v-if="
                  !this.fields.attachments[2] && !this.fields.attachments[3]
                "
              />
              <img
                src="@/assets/img/shipping/complete_attach.png"
                alt="attach"
                v-if="this.fields.attachments[3]"
              />
              <span
                class="material-icons deleteAttach"
                v-if="this.fields.attachments[3]"
                @click="removeAttach(3)"
                >cancel</span
              >
              <p v-if="this.fields.attachments[3]" class="fileName">
                {{ this.fields.attachments[3].name }}
              </p>
              <input
                type="file"
                @change="onFileChanged($event, 3)"
                :accept="getFormatAttach()"
                capture
                class="fileInput"
              />
            </div>
            <div
              :class="{
                hover: this.fields.attachments[3] || this.fields.attachments[4],
              }"
              v-if="this.nAttach >= 5"
            >
              <img
                src="@/assets/img/shipping/free_attach.png"
                alt="attach"
                v-if="this.fields.attachments[3] && !this.fields.attachments[4]"
              />
              <img
                src="@/assets/img/shipping/no_attach.png"
                alt="attach"
                v-if="
                  !this.fields.attachments[3] && !this.fields.attachments[4]
                "
              />
              <img
                src="@/assets/img/shipping/complete_attach.png"
                alt="attach"
                v-if="this.fields.attachments[4]"
              />
              <span
                class="material-icons deleteAttach"
                v-if="this.fields.attachments[4]"
                @click="removeAttach(4)"
                >cancel</span
              >
              <p v-if="this.fields.attachments[4]" class="fileName">
                {{ this.fields.attachments[4].name }}
              </p>
              <input
                type="file"
                @change="onFileChanged($event, 4)"
                :accept="getFormatAttach()"
                capture
                class="fileInput"
              />
            </div>
          </div>
        </div>
        <hr />

        <!--
          Pulsante di invio - Invia il form al server
        -->
        <div class="cdlContentButtonSingle">
          <div
            class="cdlButtonSingle green"
            :style="this.disableSubmit ? ['opacity: .5'] : ''"
            @click="submit()"
          >
            {{ $t("help.submitLabel") }}
          </div>
        </div>
        <!--
          Visualizzazione errori - Mostra messaggi di errore dal server
        -->
        <div style="margin: 20px auto; font-size: 16px" v-show="this.error">
          <span class="error">
            {{ this.error }}
          </span>
        </div>

        <!--
          Link per annullare - Torna alla pagina precedente
        -->
        <div class="deleteOrder">
          <a style="color: black" @click="this.$router.go(-1)">
            {{ $t("remoteHands.deleteTicket") }}
          </a>
        </div>
      </div>
    </div>
    <!--
      INTEGRAZIONE HUBSPOT - Form esterno caricato dinamicamente
    -->
    <div class="contentFormTwoCol" v-else>
      <!--
        Componente che mostra i dettagli della soluzione/asset (stesso del form standard)
      -->
      <HelpRequestSolution
        :name="
          this.solution && this.asset_type == 'cross-connects'
            ? this.solution.tipo
            : this.solution.name
            ? this.solution.name
            : this.solution.solution_type != 'voip'
            ? this.solution.solution_type + ' - ' + this.solution.solution
            : this.solution.solution
        "
        :address="this.solution?.address"
        :dc="this.solution?.room"
      />
      <div class="contentSecondary">
        <!--
          Avviso di ticket duplicato (stesso del form standard)
        -->
        <div class="mdl-grid padding0">
          <div
            class="mdl-cell mdl-cell--12-col alert-container"
            v-if="hasDuplicateTicket"
          >
            <span class="text-alert-danger">
              {{ $t("help.duplicatedTicketAlert") }}
              <span
                v-if="duplicateTicketIds.length"
                class="duplicate-ticket-links"
              >
                {{ $t("help.duplicatedTicketListLabel") }}
                <template v-for="(ticketId, index) in duplicateTicketIds" :key="ticketId">
                  <router-link :to="buildIncidentTicketLink(ticketId)">
                    {{ ticketId }}
                  </router-link>
                  <span v-if="index < duplicateTicketIds.length - 1">,&nbsp;</span>
                </template>
              </span>
            </span>
          </div>
        </div>
        <div class="mdl-grid padding0">
          <div class="mdl-cell mdl-cell--12-col">
            <h2 class="title">{{ $t("help.incident.formTitle") }}</h2>
          </div>
          <div class="mdl-cell mdl-cell--12-col">
            <span class="labelTwo">{{ $t("help.mainInfo") }}</span>
          </div>
          <!--
            Contenitore del form HubSpot - Carica e visualizza il form esterno
          -->
          <div class="mdl-cell mdl-cell--12-col">
            <MyLoading :viewLoader="viewLoader" type="" />
            <div class="content-hub" :style="{ opacity: opacityForm }">
              <!--
                Contenitore dove verrà iniettato il form HubSpot
              -->
              <div :id="formId" v-once></div>
            </div>
          </div>
        </div>
        <hr />
      </div>
    </div>
  </div>
</template>

<script>
/*
 * SCRIPT SECTION - Logica del componente
 */

// Importazione delle dipendenze
import { useVuelidate } from "@vuelidate/core";
import { email, requiredIf } from "@vuelidate/validators";
import HelpRequestConfirmed from "@/components/Help/HelpRequestConfirmed.vue";
import HelpRequestSolution from "@/components/Help/HelpRequestSolution.vue";
import MyLoading from "@/components/MyLoading.vue";
import {
  buildSolutionLabel,
  checkDuplicateTicketForAsset,
  getBackUrlFromAssetType,
  getIncidentSeverityLevels,
} from "@/services/TicketService.js";
import "@/assets/scss/Global.scss";
import "@/assets/scss/Help.scss";
import { useUserStore } from "@/stores/UserStore";

export default {
  // Nome del componente
  name: "IncidentForm",

  // Setup per la validazione del form
  setup() {
    return { v$: useVuelidate() };
  },

  // Componenti utilizzati
  components: {
    HelpRequestConfirmed,
    HelpRequestSolution,
    MyLoading,
  },
  // Regole di validazione del form
  validations() {
    return {
      fields: {
        // Campi obbligatori solo se non si usa HubSpot
        severity_level: {
          requiredIfFoo: requiredIf(this.isHub),
        },
        title: {
          requiredIfFoo: requiredIf(this.isHub),
        },
        description: {
          requiredIfFoo: requiredIf(this.isHub),
        },
        // Validazione formato email
        other_email: { email },
      },
    };
  },

  // Stato del componente
  data() {
    return {
      // ID dell'incidente creato (null finché non viene creato)
      incidentId: null,

      // Tipo di asset dai parametri di route
      assetTypeFromParams: null,

      // Flag per disabilitare il pulsante di invio durante l'elaborazione
      disableSubmit: false,

      // Configurazione allegati
      nAttach: 5,                // Numero massimo di allegati
      errorAttach: false,        // Errore formato allegato
      sizeAttach: 0,             // Dimensione totale allegati
      errorAttachSize: false,    // Errore dimensione allegati

      // Messaggio di errore generale
      error: "",

      // Dati per i dropdown e altre informazioni
      categories: [],            // Categorie di incidenti
      solution: {},              // Dettagli della soluzione/asset
      duplicateTicketIds: [],    // Elenco ticket duplicati già aperti
      hasDuplicateTicket: false, // Flag presenza ticket duplicati
      severityLevels: [],        // Livelli di severità disponibili

      // Campi del form
      fields: {
        title: null,             // Titolo incidente
        description: null,       // Descrizione incidente
        category: null,          // Categoria incidente
        severity_severity_level: "", // Livello severità
        other_email: "",         // Email contatto secondario
        asset: this.$route.params.asset_id, // ID asset
        solution: "",            // Descrizione soluzione
        attachments: [],         // Array allegati
      },

      // Headers per le richieste API
      he: {
        Accept: "application/json",
        Authorization: "Bearer " + this.$keycloak.idToken,
        "Content-Type": "multipart/form-data",
      },

      // URL per tornare alla lista dopo la creazione
      backToListUrl: "",

      // Flag per usare HubSpot invece del form standard
      isHub: true,

      // Configurazione HubSpot
      formId: "hubspotForm",     // ID del contenitore per il form HubSpot
      asset_type: "lines",       // Tipo di asset
      team_link: "incident_TLC", // Link al team
      assetId: 0,                // ID asset
      assetUrl: "",              // URL asset
      sla: null,                 // SLA dell'asset

      // Stato UI per HubSpot
      viewLoader: false,         // Mostra loader
      opacityForm: 0,            // Opacità form (per animazione)
    };
  },
  // Metodi del componente
  methods: {
    // Gestisce l'animazione delle label nei campi di input
    label(ev) {
      this.$LabelInput(ev);
    },

    /**
     * Metodo per inviare il form al server
     */
    async submit() {
      // Validazione del form prima dell'invio
      const isFormCorrect = await this.v$.$validate();
      if (!isFormCorrect) return false;

      // URL per la creazione dell'incidente
      const CREATE_INCIDENT_URL = "tickets/v1/incident";

      // Disabilita il pulsante per evitare invii multipli
      this.disableSubmit = true;

      // Chiamata API POST per creare l'incidente
      this.axios
        .post(
          window.VUE_APP_MISTRA_URL + CREATE_INCIDENT_URL,
          { ...this.fields, asset_type: this.assetTypeFromParams },
          { headers: this.he }
        )
        .then((res) => {
          // Gestione risposta
          if (res.status !== 200) {
            window.scrollTo(0, 0);
            this.error = res.status + ": " + res.data;
            return;
          }
          // Salva l'ID dell'incidente creato (mostra la conferma)
          this.incidentId = res.data;
        })
        .then(() => {
          // Riabilita il pulsante di invio
          this.disableSubmit = false;
        })
        .catch((err) => {
          // Gestione errori
          console.error(err);
          this.error = err.response.data.message;
          this.disableSubmit = false;
        });
    },
    getSolution() {
      this.axios
        .get(
          `${window.VUE_APP_MISTRA_URL}assets/v1/${
            this.assetTypeFromParams
          }/${this.assetId}`,
          { headers: this.he }
        )
        .then((res) => {
          if (res.status !== 200) {
            window.scrollTo(0, 0);
            this.error = res.status + ": " + res.data;
            return;
          }
          //set the solution hidden field
          this.fields.solution = res.data.length
            ? buildSolutionLabel(res.data[0])
            : buildSolutionLabel(res.data);
          // : `[Incident] - [${assetId}]`;
          this.solution = res.data[0] ? res.data[0] : res.data;
        })
        .then(() => {
          this.disableSubmit = false;
        })
        .catch((err) => {
          console.error(err);
          this.error = err.response.data.message;
        });
    },
    getCategories() {
      if (
        this.assetTypeFromParams !== "colocation" &&
        this.assetTypeFromParams !== "cloud" &&
        this.assetTypeFromParams !== "cross-connects"
      )
        return;

      let setLines = "";
      if (this.assetTypeFromParams == "colocation") {
        setLines = ",lines";
      }
      if (this.assetTypeFromParams == "cross-connects") {
        setLines = ",cross-connects";
      }

      const GET_CATEGORIES_URL =
        "front/v1/incident-categories?service=" +
        this.assetTypeFromParams +
        setLines;

      this.axios
        .get(
          window.VUE_APP_MISTRA_URL + GET_CATEGORIES_URL,

          { headers: this.he }
        )
        .then((res) => {
          if (res.status !== 200) {
            window.scrollTo(0, 0);
            this.error = res.status + ": " + res.data;
            return;
          }
          //go to ticket detail
          this.categories = res.data;
        })
        .catch((err) => {
          console.error(err);
          this.error = err.response.data.message;
        });
    },
    onFileChanged($event, index) {
      const target = $event.target;
      if (target && target.files) {
        if (
          this.getFormatAttach().search(target.files[0].type) < 0 ||
          target.files[0].type == ""
        ) {
          this.errorAttach = true;
          return;
        }

        this.errorAttach = false;
        this.errorAttachSize = false;

        this.sizeAttach += parseFloat(this.getSize(target.files[0].size));

        if (this.sizeAttach > this.getLimitSizeAttach()) {
          this.errorAttachSize = true;
          return;
        }

        this.fields.attachments[index] = target.files[0];
      }
    },
    checkDuplicateForAsset() {
      checkDuplicateTicketForAsset(
        this.assetTypeFromParams,
        this.$route.params.asset_id,
        "incident",
        this.he
      )
        .then((res) => {
          const data = res.data || {};
          const ids = Array.isArray(data.duplicateTicketIds)
            ? data.duplicateTicketIds
            : data.duplicated
            ? [data.duplicated]
            : [];

          this.duplicateTicketIds = ids;
          this.hasDuplicateTicket = Boolean(data.isDuplicated || ids.length);

          if (!this.hasDuplicateTicket) {
            this.duplicateTicketIds = [];
          }
        })
        .catch((error) => {
          console.error("Duplicate ticket check failed", error);
          this.hasDuplicateTicket = false;
          this.duplicateTicketIds = [];
        });
    },
    buildIncidentTicketLink(ticketId) {
      return `/${this.$t("nav.incidentListUrl")}/${this.$t("nav.detail")}/${ticketId}`;
    },
    getSeverityLevels() {
      getIncidentSeverityLevels(this.he).then((res) => {
        this.severityLevels = res.data;
      });
    },
    removeAttach(index) {
      this.fields.attachments.splice(index, 1);
    },
    getSize(sizeInBytes) {
      return (sizeInBytes / (1024 * 1024)).toFixed(2);
    },
    getLimitSizeAttach() {
      return window.VUE_APP_LIMIT_SIZE_ATTACH;
    },
    getFormatAttach() {
      return window.VUE_APP_TYPE_ATTACH;
    },
    getFormatNameAttach() {
      return window.VUE_APP_TYPE_ATTACH_NAME.replaceAll("&nbsp;", " ");
    },
    /**
     * Metodo hub() - Gestisce l'integrazione con HubSpot
     *
     * Questo metodo è responsabile di:
     * 1. Caricare il form HubSpot
     * 2. Personalizzare il form con CSS
     * 3. Precompilare i campi con i dati dell'utente e dell'asset
     * 4. Inviare automaticamente il form
     */
    hub() {
      // Verifica se il contenitore del form esiste
      if (document.getElementById(this.formId)) {
        // Rimuove l'iframe esistente se presente (per ricaricare il form)
        if (
          document.getElementById(this.formId).getElementsByTagName("iframe")[0]
        ) {
          document
            .getElementById(this.formId)
            .getElementsByTagName("iframe")[0]
            .remove();
        }

        // Ottiene informazioni sull'utente e sull'account
        let infoAccount = useUserStore().user.account;
        let infoUser = useUserStore().user;

        // Prepara il CSS personalizzato da iniettare nell'iframe
        let theme =
          '<link type="text/css" rel="stylesheet" href="../../css/hubspot.css">';
        if (this.assetId > 0) {
          theme =
            '<link type="text/css" rel="stylesheet" href="../../../css/hubspot.css">';
        }

        // Carica il form HubSpot
        this.loadForm().then(() => {
          setTimeout(() => {
            // Ottiene riferimento all'iframe creato da HubSpot
            let iFrame = document
              .getElementById(this.formId)
              .getElementsByTagName("iframe")[0];

            // Inietta il CSS personalizzato nell'iframe
            iFrame.contentWindow.document.head.innerHTML += theme;

            // Ottiene riferimento al form all'interno dell'iframe
            let form =
              iFrame.contentWindow.document.body.getElementsByTagName(
                "form"
              )[0];

            // Nasconde il campo categoria se non è un asset di tipo colocation
            if (this.$route.params.asset_type != "colocation") {
              let categoria_incident = form.getElementsByClassName(
                "hs_TICKET.categoria_incident"
              )[0];
              categoria_incident.style.display = "none";
            }

            // Imposta un intervallo per assicurarsi che il form sia completamente caricato
            let intervalForm = setInterval(() => {
              if (form) {
                // Nasconde nuovamente il campo categoria (per sicurezza)
                if (this.$route.params.asset_type != "colocation") {
                  let categoria_incident = form.getElementsByClassName(
                    "hs_TICKET.categoria_incident"
                  )[0];
                  categoria_incident.style.display = "none";
                }

                // PRECOMPILAZIONE DEI CAMPI DEL FORM

                // Codice azienda
                let numero_azienda = form.querySelector(
                  'input[name="TICKET.codice_azienda_alyante"]'
                );
                numero_azienda.value = infoAccount.id;
                numero_azienda.dispatchEvent(
                  new Event("input", { bubbles: true })
                );
                numero_azienda.dispatchEvent(
                  new Event("change", { bubbles: true })
                );

                // Nome azienda
                let nome_azienda = form.querySelector(
                  'input[name="TICKET.azienda_provenienza_ticket"]'
                );
                nome_azienda.value = infoAccount.business_name;
                nome_azienda.dispatchEvent(
                  new Event("input", { bubbles: true })
                );
                nome_azienda.dispatchEvent(
                  new Event("change", { bubbles: true })
                );

                // Tipo di asset (con conversione dei nomi)
                let asset_type = form.querySelector(
                  'input[name="TICKET.tipologia_asset"]'
                );

                let assetType = this.asset_type;
                if (assetType == "lines") {
                  assetType = "accesso";
                }

                if (assetType == "cross-connects") {
                  assetType = "cross_connect";
                }

                if (this.asset_type == "voip") {
                  assetType = "VOIP";
                }

                asset_type.value = assetType;
                asset_type.dispatchEvent(new Event("input", { bubbles: true }));
                asset_type.dispatchEvent(
                  new Event("change", { bubbles: true })
                );

                // Email utente
                let email = form.querySelector('input[name="email"]');
                email.value = infoUser.email;
                email.dispatchEvent(new Event("input", { bubbles: true }));
                email.dispatchEvent(new Event("change", { bubbles: true }));

                // Link al team
                let servizio_link_team = form.querySelector(
                  'input[name="TICKET.servizio_link_team"]'
                );
                servizio_link_team.value = this.team_link;
                servizio_link_team.dispatchEvent(
                  new Event("input", { bubbles: true })
                );
                servizio_link_team.dispatchEvent(
                  new Event("change", { bubbles: true })
                );

                // Soluzione impattata (combinazione di nome e indirizzo)
                let mySol =
                  this.solution && this.asset_type == "cross-connects"
                    ? this.solution.tipo
                    : this.solution.name
                    ? this.solution.name
                    : this.solution.solution_type != "voip"
                    ? this.solution.solution_type +
                      " - " +
                      this.solution.solution
                    : this.solution.solution;
                let myAddr = "";

                if (this.asset_type == "lines") {
                  myAddr = this.solution?.address;
                }

                let soluzione_impattata = form.querySelector(
                  'input[name="TICKET.soluzione_impattata"]'
                );
                soluzione_impattata.value = mySol + " " + myAddr;
                soluzione_impattata.dispatchEvent(
                  new Event("input", { bubbles: true })
                );
                soluzione_impattata.dispatchEvent(
                  new Event("change", { bubbles: true })
                );

                // ID dell'asset
                let assetId = form.querySelector(
                  'input[name="TICKET.asset_id"]'
                );
                assetId.value = this.assetId;
                assetId.dispatchEvent(new Event("input", { bubbles: true }));
                assetId.dispatchEvent(new Event("change", { bubbles: true }));

                // URL dell'asset
                let assetUrl = form.querySelector(
                  'input[name="TICKET.asset_manager_url"]'
                );
                assetUrl.value = this.assetUrl;
                assetUrl.dispatchEvent(new Event("input", { bubbles: true }));
                assetUrl.dispatchEvent(new Event("change", { bubbles: true }));

                // SLA dell'asset
                let sla = form.querySelector(
                  'input[name="TICKET.sla_asset_manager"]'
                );
                sla.value = this.sla;
                sla.dispatchEvent(new Event("input", { bubbles: true }));
                sla.dispatchEvent(new Event("change", { bubbles: true }));

                // Invia automaticamente il form
                form.dispatchEvent(new Event("submit", { cancelable: true }));

                // Ferma l'intervallo di controllo
                clearInterval(intervalForm);

                // Aggiorna l'UI per mostrare il form
                this.opacityForm = 1;
                this.viewLoader = true;

                // Codice commentato per gestire la conferma di invio
                // const myDiv = form.querySelector('input[type="submit"]');
                // const self = this;
                // myDiv.addEventListener('click', function() {
                //   setInterval(() => {
                //     if( !self.formSend ) {
                //       if( iFrame.contentWindow.document.body.getElementsByClassName('submitted-message')[0] ) {
                //         if( iFrame.contentWindow.document.body.getElementsByClassName('submitted-message')[0].innerHTML == "<p>Grazie per aver compilato il modulo.</p>" ) {
                //           self.openModal();
                //         }
                //       }
                //     }
                //   }, 1000);
                // });
              }
            }, 500);
          }, 500);
        });
      }
    },
    openModal() {
      this.$emit("openModal", { type: "confirmIncident" });
    },
    loadForm() {
      let formId = window.VUE_APP_HB_INCIDENT_FORM_EN;
      if (this.$i18n.locale == "it") {
        formId = window.VUE_APP_HB_INCIDENT_FORM_IT;
      }
      return new Promise((resolve) => {
        const script = document.createElement("script");
        script.src = "https://js.hsforms.net/forms/v2.js";
        document.body.appendChild(script);
        script.addEventListener("load", () => {
          try {
            if (window.hbspt) {
              window.hbspt.forms.create({
                region: "eu1",
                portalId: window.VUE_APP_HB_PORTAL_ID,
                formId: formId,
                target: "#" + this.formId,
                onFormSubmitted: this.openModal,
              });
              setTimeout(() => {
                resolve(true);
              }, 800);
            }
          } catch (error) {
            console.error("ERRORE:", error);
          }
        });
      });
    },
    getSla() {
      const GET_CATEGORIES_URL =
        "assets/v1/asset-sla-info?asset=" +
        this.assetId +
        "&asset_type=" +
        this.asset_type;

      this.axios
        .get(
          window.VUE_APP_MISTRA_URL + GET_CATEGORIES_URL,

          { headers: this.he }
        )
        .then((res) => {
          if (res.status !== 200) {
            window.scrollTo(0, 0);
            this.error = res.status + ": " + res.data;
            return;
          }
          this.assetUrl = res.data.asset_url;
          this.sla = res.data.sla;
          // //go to ticket detail
          // this.categories = res.data;
        })
        .catch((err) => {
          console.error(err);
          this.error = err.response.data.message;
        });
    },
    // comeBack() {
    //   window.scrollTo({ top: 0, behavior: "smooth" });
    //   setTimeout(() => {
    //     this.$router.push({
    //       name: this.backToListUrl,
    //     })
    //   }, 5000);
    // }
  },
  mounted() {
    window.scrollTo({ top: 0, behavior: "smooth" });

    this.assetId = this.$route.params.asset_id;

    if (this.isHub) {
      this.asset_type = this.$route.params.asset_type;
      this.team_link = this.$route.meta.team_link;
      this.hub();
      this.getSla();
    } else {
      // get incident categories
      this.getCategories();
      // get incident gravity levels
      this.getSeverityLevels();
    }

    // retrieve backUrl from assetType param
    this.backToListUrl = getBackUrlFromAssetType(this.$route.params.asset_type);
    // get assetType and asset id from route
    this.assetTypeFromParams = this.$route.params.asset_type;

    // get solution
    this.getSolution();
    // get duplicated tickets
    this.checkDuplicateForAsset();
  },
};
</script>
